import * as signalR from "@microsoft/signalr";

/** Utente come lo manda il server (ruoli "technician"/"expert", stati "offline"/"online"/"in_call"). */
export interface UserInfo {
  id: string;
  name: string;
  department: string;
  role: "technician" | "expert";
  status: "offline" | "online" | "in_call";
}

export interface CallerInfo { id: string; name: string; department: string }

export interface IceServer { urls: string[]; username?: string; credential?: string }

/** Documento condiviso in chiamata (POST /api/calls/{id}/documents, evento DocumentAvailable). */
export interface SharedDocument { id: string; callId: string; fileName: string; senderName: string; pageCount: number; createdAt: string }

export interface AssistEvents {
  incomingCall(callId: string, from: CallerInfo): void;
  callAccepted(callId: string): void;
  callRejected(callId: string, reason: string): void;
  callEnded(callId: string, reason: string): void;
  remoteSdp(callId: string, type: "offer" | "answer", sdp: string): void;
  remoteIce(callId: string, candidate: string, sdpMid: string | null, sdpMLineIndex: number | null): void;
  presenceChanged(userId: string, status: UserInfo["status"]): void;
  documentAvailable(callId: string, document: SharedDocument): void;
  connectionChanged(state: "connected" | "reconnecting" | "disconnected"): void;
}

/**
 * Client dell'hub `/hubs/assist` (MILK SERVER): presenza, chiamata e segnalazione WebRTC.
 * Un'istanza per sessione; l'URL base è relativo (stessa origine) salvo diversa indicazione.
 */
export class AssistClient {
  private conn: signalR.HubConnection | null = null;

  /** `token`: JWT del login (POST /api/auth/login). SignalR lo manda come ?access_token= (WebSocket) o header Bearer. */
  constructor(private readonly baseUrl: string, private readonly token: string, private readonly events: Partial<AssistEvents>) {}

  async connect(): Promise<void> {
    const conn = new signalR.HubConnectionBuilder()
      .withUrl(`${this.baseUrl}/hubs/assist`, { accessTokenFactory: () => this.token })
      .withAutomaticReconnect([0, 1000, 3000, 5000, 10000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    const e = this.events;
    conn.on("IncomingCall", (id: string, from: CallerInfo) => e.incomingCall?.(id, from));
    conn.on("CallAccepted", (id: string) => e.callAccepted?.(id));
    conn.on("CallRejected", (id: string, reason: string) => e.callRejected?.(id, reason));
    conn.on("CallEnded", (id: string, reason: string) => e.callEnded?.(id, reason));
    conn.on("RemoteSdp", (id: string, type: "offer" | "answer", sdp: string) => e.remoteSdp?.(id, type, sdp));
    conn.on("RemoteIce", (id: string, c: string, mid: string | null, idx: number | null) => e.remoteIce?.(id, c, mid, idx));
    conn.on("PresenceChanged", (userId: string, status: UserInfo["status"]) => e.presenceChanged?.(userId, status));
    conn.on("DocumentAvailable", (callId: string, d: SharedDocument) => e.documentAvailable?.(callId, d));
    conn.onreconnecting(() => e.connectionChanged?.("reconnecting"));
    conn.onreconnected(() => e.connectionChanged?.("connected"));
    conn.onclose(() => e.connectionChanged?.("disconnected"));

    await conn.start();
    this.conn = conn;
    e.connectionChanged?.("connected");
  }

  async disconnect(): Promise<void> {
    await this.conn?.stop();
    this.conn = null;
  }

  private get hub(): signalR.HubConnection {
    if (!this.conn) throw new Error("Non connesso al server");
    return this.conn;
  }

  listUsers(role?: UserInfo["role"]): Promise<UserInfo[]> { return this.hub.invoke("ListUsers", role ?? null); }
  startCall(calleeId: string): Promise<string> { return this.hub.invoke("StartCall", calleeId); }
  acceptCall(callId: string): Promise<void> { return this.hub.invoke("AcceptCall", callId); }
  rejectCall(callId: string, reason?: string): Promise<void> { return this.hub.invoke("RejectCall", callId, reason ?? null); }
  endCall(callId: string): Promise<void> { return this.hub.invoke("EndCall", callId); }
  sendSdp(callId: string, type: "offer" | "answer", sdp: string): Promise<void> { return this.hub.invoke("SendSdp", callId, type, sdp); }
  sendIce(callId: string, candidate: string, sdpMid: string | null, sdpMLineIndex: number | null): Promise<void> {
    return this.hub.invoke("SendIce", callId, candidate, sdpMid, sdpMLineIndex);
  }

  /** STUN/TURN con credenziali temporanee, da chiedere prima di creare la PeerConnection. */
  async getIceServers(callId: string): Promise<IceServer[]> {
    const r = await fetch(`${this.baseUrl}/api/calls/${callId}/ice-servers`, { headers: { Authorization: `Bearer ${this.token}` } });
    if (!r.ok) throw new Error(`ice-servers: HTTP ${r.status}`);
    const body = (await r.json()) as { iceServers: IceServer[] };
    return body.iceServers;
  }
}

/** Messaggio che pulisce l'errore di HubException ("An unexpected error occurred invoking 'StartCall' on the server. HubException: busy"). */
export function hubErrorMessage(e: unknown): string {
  const m = String((e as Error)?.message ?? e);
  const i = m.indexOf("HubException: ");
  return i >= 0 ? m.slice(i + "HubException: ".length) : m;
}
